import { ChevronDownIcon } from '@heroicons/react/outline';
import { Badge, Link, Pagination, Table } from '@yoast/ui-library';
import { useEffect, useMemo, useState } from 'react';

import type { QueryResultDetailedCitationsItem } from '../../api/generated/insights.schemas';
import createComponent from '../../components/CreateComponent';

interface CitationsSectionProps {
  id?: string;
  citations: QueryResultDetailedCitationsItem[];
}

const MobileOnly = createComponent`md:yst-hidden yst-mr-2 yst-font-medium yst-text-slate-400 yst-inline-flex yst-w-20`;

const CITATIONS_PER_PAGE = 10;

const CitationsSection = ({ id, citations }: CitationsSectionProps) => {
  const [citationsPage, setCitationsPage] = useState(1);

  const sortedCitations = useMemo(
    () => [...citations].sort((a, b) => a.rank - b.rank),
    [citations],
  );

  const citationsTotalPages = Math.ceil(sortedCitations.length / CITATIONS_PER_PAGE);
  const citationsStartIndex = (citationsPage - 1) * CITATIONS_PER_PAGE;
  const citationsEndIndex = citationsStartIndex + CITATIONS_PER_PAGE;
  const currentCitations = sortedCitations.slice(citationsStartIndex, citationsEndIndex);

  useEffect(() => {
    if (citationsPage > citationsTotalPages && citationsTotalPages > 0) {
      setCitationsPage(1);
    }
  }, [citationsTotalPages, citationsPage]);

  if (citations.length === 0) {
    return null;
  }

  return (
    <details id={id} className="yst-group/citations yst-mt-6" open>
      <summary className="yst-flex yst-items-center yst-justify-between yst-cursor-pointer yst-list-none [&::-webkit-details-marker]:yst-hidden yst-mb-4">
        <h3 className="yst-font-semibold">Citations ({citations.length})</h3>
        <ChevronDownIcon className="yst-h-4 yst-w-4 yst-text-gray-400 yst-flex-shrink-0 group-open/citations:yst-rotate-180 yst-transition-transform" />
      </summary>
      <Table>
        <Table.Head>
          <Table.Row>
            <Table.Header className="yst-hidden md:yst-table-cell">{'\u00A0'}</Table.Header>
            <Table.Header className="yst-hidden md:yst-table-cell">URL</Table.Header>
            <Table.Header className="yst-hidden md:yst-table-cell">Root domain</Table.Header>
            <Table.Header className="yst-hidden md:yst-table-cell">Brands</Table.Header>
          </Table.Row>
        </Table.Head>
        <Table.Body className="max-md:yst-border-t-0">
          {currentCitations.map((citation, index) => (
            <tr key={`${citation.url}-${index}`} className="yst-flex yst-flex-col yst-py-4 md:yst-table-row md:yst-py-0">
              <Table.Cell className="yst-px-4 yst-py-1 md:yst-p-2 md:yst-pl-4">
                <MobileOnly as="span">Rank:</MobileOnly>
                #{citation.rank}
              </Table.Cell>
              <Table.Cell className="yst-break-all yst-px-4 yst-py-1 md:yst-p-2 md:yst-w-1/2">
                <MobileOnly as="span">URL:</MobileOnly>
                <Link href={citation.url} target="_blank" rel="noopener noreferrer">
                  {citation.url}
                </Link>
              </Table.Cell>
              <Table.Cell className="yst-px-4 yst-py-1 md:yst-p-2">
                <MobileOnly as="span">Domain:</MobileOnly>
                {citation.rootDomain}
              </Table.Cell>
              <Table.Cell className="yst-px-4 yst-py-1 md:yst-p-2">
                <MobileOnly as="span">Brands:</MobileOnly>
                {citation.associatedBrands.length > 0 ? (
                  <div className="yst-flex yst-flex-wrap yst-gap-1">
                    {citation.associatedBrands.map((brand) => (
                      <Badge
                        key={brand.id}
                        variant={brand.isSelf ? 'ai' : 'plain'}
                      >
                        {brand.name}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <span className="yst-text-gray-500">None</span>
                )}
              </Table.Cell>
            </tr>
          ))}
        </Table.Body>
      </Table>
      {sortedCitations.length > CITATIONS_PER_PAGE && (
        <div className="yst-flex yst-items-center yst-justify-end yst-gap-4 yst-mt-4">
          <p className="yst-mb-0">
            Showing {citationsStartIndex + 1} to{' '}
            {citationsEndIndex > sortedCitations.length ? sortedCitations.length : citationsEndIndex} of{' '}
            {sortedCitations.length} results
          </p>
          <Pagination
            variant="text"
            className="yst-flex-row yst-gap-0"
            current={citationsPage}
            total={citationsTotalPages}
            onNavigate={setCitationsPage}
            screenReaderTextPrevious="Previous page"
            screenReaderTextNext="Next page"
          />
        </div>
      )}
    </details>
  );
};

export default CitationsSection;
